import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ContractorQueueTable } from 'src/components/document-review/queue/ContractorQueueTable'
import { DocumentReviewTabs } from 'src/components/document-review/queue/DocumentReviewTabs'
import { DocumentReviewToolbar } from 'src/components/document-review/queue/DocumentReviewToolbar'
import { McQueueTable } from 'src/components/document-review/queue/McQueueTable'
import { NoAccessCard } from 'src/components/shared/NoAccessCard'
import {
  AdminFetchCustomers,
  AdminFetchDocumentReviewSteps,
  AdminFetchMcReviewDocuments,
} from 'src/constants/actionTypes'
import { DocumentReviewTab } from 'src/constants/document-review/enums'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCustomers } from 'src/selectors/customer'
import { resolveDocumentReviewQueueTab } from 'src/utils/documentReviewQueueTab'

const TAB_QUERY_PARAM = 'tab'

/**
 * Laid out like the customer list: an h2, then the table card — tabs, filter row, grid — as one
 * white block filling the rest of the viewport.
 *
 * The selected tab lives in the query string rather than component state so that returning from a
 * review screen lands the reviewer on the tab they left.
 *
 * Each tab is shown, and its queue fetched, only with that queue's view permission (ENG-1207
 * part 2). The app route lets in anyone holding either, so "neither" only happens on a stale
 * identity — it renders a no-access card rather than an empty tab strip.
 */
const DocumentReviewQueuePageComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button; included in the fetch effect's dependency array below.
  const [refreshToken, setRefreshToken] = useState<number>(0)

  const dispatch = useDispatch()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const customers = useSelector(getCustomers)

  const canViewContractor = useHasPermission(InternalPermission.DocumentReviewView)
  const canViewMc = useHasPermission(InternalPermission.McDocumentReviewView)
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  const tabParam = searchParams.get(TAB_QUERY_PARAM)

  const selectedTab = useMemo(() => {
    return resolveDocumentReviewQueueTab(tabParam, canViewContractor, canViewMc)
  }, [canViewContractor, canViewMc, tabParam])

  const availableTabs = useMemo(() => {
    const tabs: DocumentReviewTab[] = []

    if (canViewContractor) {
      tabs.push(DocumentReviewTab.Contractor)
    }

    if (canViewMc) {
      tabs.push(DocumentReviewTab.MasterContractor)
    }

    return tabs
  }, [canViewContractor, canViewMc])

  const handleTabChange = useCallback(
    (tab: DocumentReviewTab) => {
      const nextParams = new URLSearchParams(searchParams)

      nextParams.set(TAB_QUERY_PARAM, tab)
      // Replace rather than push: flipping a tab is not a navigation a reviewer wants to walk
      // back through with the browser's Back button.
      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // Both queues load whichever tab is open — both tab subtitles show counts — but each only with
  // its own view permission, so a reviewer scoped to one queue never sees a 403 toast.
  useEffect(() => {
    if (!isAuthenticated || !canViewContractor) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchDocumentReviewSteps.request())
  }, [asyncDispatchWithErrorToast, canViewContractor, isAuthenticated, refreshToken])

  useEffect(() => {
    if (!isAuthenticated || !canViewMc) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchMcReviewDocuments.request())
  }, [asyncDispatchWithErrorToast, canViewMc, isAuthenticated, refreshToken])

  // The MC rows carry only `partnerId`, so the customer list supplies the display name. Fetched
  // only when the store is empty: it is a big list and other screens populate it too.
  //
  // This call needs `partners_view`, a DIFFERENT permission from `mc_document_review_view`, so it
  // is skipped without it and the name column falls back to the partner id
  // (`resolveMcDisplayName`). Still a plain `dispatch` rather than `asyncDispatchWithErrorToast`:
  // a failure here is not worth a toast on a page that still works.
  useEffect(() => {
    if (!isAuthenticated || !canViewPartners || customers.length > 0) {
      return
    }

    dispatch(AdminFetchCustomers.request())
  }, [dispatch, canViewPartners, isAuthenticated, customers.length])

  return (
    // A flex column so the grid takes exactly the height left under the header, tabs and filter
    // row. `min-h-0` lets the page shrink to its column, and `overflow-hidden` means the grid
    // body is the only thing that scrolls — never the page behind it as well.
    <div
      data-testid="document-review-queue-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <div css={tw`mb-6 flex shrink-0 flex-row items-center justify-between gap-4`}>
        <Typography variant="h2">Internal Document Review</Typography>
      </div>
      {selectedTab === null ? (
        <NoAccessCard
          message="You don't have access to contractor or master-contractor document review."
          dataTestId="document-review-no-access"
        />
      ) : null}
      {selectedTab !== null ? (
        <div css={tw`shrink-0`}>
          <DocumentReviewTabs
            selectedTab={selectedTab}
            availableTabs={availableTabs}
            onChange={handleTabChange}
          />
          <DocumentReviewToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onRefresh={handleRefresh}
          />
        </div>
      ) : null}
      {selectedTab === DocumentReviewTab.Contractor ? (
        <ContractorQueueTable searchQuery={searchQuery} />
      ) : null}
      {selectedTab === DocumentReviewTab.MasterContractor ? (
        <McQueueTable searchQuery={searchQuery} />
      ) : null}
    </div>
  )
}

export const DocumentReviewQueuePage = memo(DocumentReviewQueuePageComponent)
